import styles from './Submenu.module.css'
import IconCross from '@/assets/img/Icon_Cross.svg'
import IconCheck from '@/assets/img/Icon_Check.svg'
import IconAddUser from '@/assets/img/Icon_Add-User.svg'
import IconUser from '@/assets/img/Icon_User.svg'
import IconHelp from '@/assets/img/Icon_Question.svg'
import IconSavedOn from '@/assets/img/Icon_Saved-on.svg'
import IconGameHistory from '@/assets/img/Icon_Game-history.svg'
import IconGamepads from '@/assets/img/Icon_VPad.svg'
import IconOurApps from '@/assets/img/Icon_OurApps.svg'
import IconExit from '@/assets/img/Icon_Exit.svg'
import IconSpeedtest from '@/assets/img/Icon_Speed-Test.svg'
import IconButton, { IconButtonKind, IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import { useTranslations } from 'next-intl'
import Avatar, { AvatarSizes } from '@/components/UI/Avatar/Avatar'
import { useUserStore } from '@/stores/useUserStore'
import SubmenuItem from '../../UI/SubmenuItem/SubmenuItem'
import { useOutsideAlerter } from '@/hooks/useOutsideAlerter'
import { useMemo, useRef, useState } from 'react'
import LogoutModal from '@/components/Modals/LogoutModal/LogoutModal'
import { useModalStore } from '@/stores/useModalStore'
import { ProfileType } from '@/interfaces/profile_types'
import { useProfiles } from '@/apiUtilities/queries/useProfiles'
import { useSelf } from '@/apiUtilities/queries/useSelf'
import LinkButton, { LinkButtonKind, LinkButtonSize } from '@/components/UI/Buttons/LinkButton/LinkButton'
import { FEATURES } from '@/utils/constants'
import { useDynamicUnsubscribeURL } from '@/apiUtilities/queries/useDynamicUnsubscribeURL'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import DeleteAccountModal from '@/components/Modals/DeleteAccountModal/DeleteAccountModal'
import GamepadsModal from '@/components/Modals/GamepadsModal/GamepadsModal'
import { usePadsStore } from '@/stores/usePadsStore'
import VPadPairingModal from '@/components/Modals/VPadPairingModal/VPadPairingModal'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'
import { PadSessionManager } from '@/services/VirtualPad/PadSessionManager'
import { VPadStage } from '@/interfaces/virtualPad_types'
import SpeedTest from '@/components/SpeedTest/SpeedTest'

interface SubmenuProps {
  onClose: () => void
}

export default function Submenu ({ onClose }: SubmenuProps) {
  const t = useTranslations('Submenu')
  const te = useTranslations('Errors')
  const { openModal, closeModal } = useModalStore()
  const physicalPadsRecord = usePadsStore((state) => state.physicalPads)
  const vpadsRecord = usePadsStore((state) => state.vpads)
  const { profileId, setProfile } = useUserStore()
  const elemRef = useRef<HTMLDivElement>(null)
  useOutsideAlerter(elemRef, () => onClose())
  const { data: profiles, isSuccess: isProfilesSuccess } = useProfiles()
  const { data: self, isSuccess: isSelfSuccess } = useSelf()
  const { data: dynamicUnsubscribeURLData } = useDynamicUnsubscribeURL(self?.id, {
    enabled: FEATURES.unsubscribeExternal && FEATURES.unsubscribeExternalDynamic
  })
  const [connectingVPadSession, setConnectingVPadSession] = useState(false)

  const physicalPads = Object.values(physicalPadsRecord).sort((a, b) => a.index - b.index)
  const vpads = Object.values(vpadsRecord)
  const availableVPads = vpads.filter((vPad) => vPad.stage === VPadStage.CONNECTED || vPad.stage === VPadStage.SUSPENDED)

  const helpCenterLink = process.env.NEXT_PUBLIC_HELP_CENTER_LINK ?? ''

  const unsubscribeExternalLink = useMemo(() => {
      if (!FEATURES.unsubscribeExternal) return ''
      if (!FEATURES.unsubscribeExternalDynamic) {
        return process.env.NEXT_PUBLIC_FEATURE_UNSUBSCRIBE_EXTERNAL_LINK ?? ''
      }
      return dynamicUnsubscribeURLData?.url || ''
    }, [dynamicUnsubscribeURLData])

  /**
   * Changes the current user profile.
   * @param profileId Profile identifier
   */
  function handleChangeProfile (profileId: string) {
    setProfile(profileId)
  }

  /**
   * Logs out the current user and closes the submenu.
   */
  function handleSpeedtest () {
    onClose()
    openModal({
      content: <SpeedTest />
    })
  }

  /**
   * Logs out the current user and closes the submenu.
   */
  function handleLogout () {
    onClose()
    openModal({
      content: <LogoutModal onClose={closeModal} />
    })
  }
  
  let maxProfiles = 1
  if(isSelfSuccess && isProfilesSuccess && self.product) maxProfiles = self.product.max_profiles

  function openHelpChat() {
    window.fcWidget.open()
    onClose()
  }

  /**
   * Handles the click on banner
   */
  async function handleGamepadClick () {
    if (connectingVPadSession) return

    try {
      setConnectingVPadSession(true)
      
      await PadSessionManager.getInstance().startSession()

      if (physicalPads.length > 0 || availableVPads.length > 0) showGamepadsModal()
      else showVPadPairingModal()
    } catch(error) {
      toast.error(<Notification text={te('defaultUnknowError')} />)
    } finally {
      setConnectingVPadSession(false)
      onClose()
    }
  }

  /**
   * Show the gamepads modal
   */
  function showGamepadsModal () {
    openModal({
      type: ModalTypes.LATERAL,
      content: <GamepadsModal />
    })
  }

  /**
   * Show the virtual pad pairing modal
   */
  function showVPadPairingModal () {
    openModal({
      type: ModalTypes.LATERAL,
      content: <VPadPairingModal />
    })
  }

  /**
   * Show the delete acccount modal
   */
  function showDeleteAccountModal () {
    openModal({
      type: ModalTypes.LATERAL,
      content: <DeleteAccountModal />
    })
    onClose()
  }

  return (
    <div className={styles.container} ref={elemRef}>
      <div className={styles.closeWrapper}>
        <IconButton
          icon={IconCross}
          size={IconButtonSize.M}
          kind={IconButtonKind.EMPTY}
          onClick={onClose}
        />
      </div>

      <div className={styles.profilesWrapper}>
        <div className={styles.profilesWrapper__firstRow}>
          <p className={'bodyTextLargeStrong'}>{t('changeProfile')}</p>
          {FEATURES.addProfile && isSelfSuccess && isProfilesSuccess && profiles.length < maxProfiles &&
          <LinkButton
            href={'/add-profile'}
            size={LinkButtonSize.SMALL}
            kind={LinkButtonKind.SUGGESTED}
            onClick={() => onClose()}
          >
            <IconAddUser className={styles.addProfileIcon} /> {t('addProfileBtn')}
          </LinkButton>
          }
        </div>

        <div className={styles.profilesWrapper__secondRow}>
          {profiles !== undefined && profiles.map((profile: ProfileType) => {
            const isSelected = profile.id === profileId
            return (
            <div
              key={profile.id}
              className={`${styles.profileWrapper} ${isSelected ? styles.selected : ''}`}
              onClick={() => {handleChangeProfile(profile.id); onClose() }}
              tabIndex={0}
              role={'button'}
            >
              <Avatar
                backgroundUrl={`${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}${profile.avatar.image_path}`}
                size={AvatarSizes.S}
                selected={isSelected}
                icon={isSelected ? IconCheck : undefined}
                focuseable={false}
              />
              <p className={`${isSelected ? 'bodyTextMediumStrong' : 'bodyTextMedium'} ${styles.profileName}`}>
                {profile.name}
              </p>
            </div>
            )
          })}
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <SubmenuItem
          href={'/account-settings/edit-profile'}
          content={t('accountSettings')}
          icon={IconUser}
          onClick={onClose}
          testId={`submenu_item_account-settings`}
        />

        <SubmenuItem
          href={'/catalogue?favorite=true'}
          content={t('saved')}
          icon={IconSavedOn}
          onClick={onClose}
          testId={`submenu_item_favourites`}
        />

        <SubmenuItem
          href={'/game-history'}
          content={t('gamesHistory')}
          icon={IconGameHistory}
          onClick={onClose}
          testId={`submenu_item_game-history`}
        />

        {FEATURES.virtualPads &&
        <SubmenuItem
          content={t('gamepads')}
          icon={IconGamepads}
          isNew={true}
          onClick={handleGamepadClick}
        />
        }

        <SubmenuItem
          href={'/our-apps'}
          content={t('ourApps')}
          icon={IconOurApps}
          onClick={onClose}
          testId={`submenu_item_our-apps`}
        />

        {FEATURES.speedTest &&
        <SubmenuItem
          content={t('speedtest')}
          icon={IconSpeedtest}
          onClick={handleSpeedtest}
          testId={`submenu_item_speedtest`}
        />
        }

        {/* FEATURES.helpCenter &&
        <SubmenuItem
          href={helpCenterLink.length > 0 ? helpCenterLink : undefined}
          content={t('helpCenter')}
          icon={IconHelp}
          target={helpCenterLink.length > 0 ? '_blank' : undefined}
          onClick={helpCenterLink.length > 0 ? onClose : openHelpChat}
          testId={`submenu_item_help-center`}
        />
        */}

        {FEATURES.unsubscribe && FEATURES.unsubscribeSubmenu &&
        <SubmenuItem
          href={unsubscribeExternalLink.length > 0 ? unsubscribeExternalLink : undefined }
          content={t('unsubscribe')}
          icon={IconHelp}
          target={unsubscribeExternalLink.length > 0 ? '_blank' : undefined}
          onClick={() => {
            if (FEATURES.unsubscribeExternal) onClose()
            else showDeleteAccountModal()
          }}
        />
        }

        <SubmenuItem
          content={t('logout')}
          icon={IconExit}
          onClick={handleLogout}
          testId={`submenu_item_logout`}
        />
      </div>
    </div>
  )
}